import { type WebLegacyWidgetEntity } from '@web/types';
import type { JssStyle } from 'jss';
import {
    EButtonStyleType,
    EButtonWidthType,
    EFontStyle,
    EHorizontalPosition,
    ESize,
    InputBorderEnum,
} from '@common/enums';
import { getBaseButtonData, getBaseButtonIconStyles } from '@web/widgets/common/base/base-button/base-button.styles';
import {
    type ButtonSettings,
    CatalogCategoriesView,
    type CatalogProductsWidgetData,
    type GeneralFilterSettings,
    type PriceFilterSettings,
    type Settings,
} from '@web/widgets/catalog-products/type/catalog-products-widget.type';
import { commonWebsitesFormButtonTextStyles } from '@common/constants/button-sizes.const';
import { Attachment } from '@web/widgets/catalog-products/type';
import { themeColorCss, themeFontCss } from '@sites/site-theme/src/utils/variables';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { ThemeFontProperties } from '@sites/site-theme/src/enums/font-properties.enum';

export const paginationSizeStyles: Record<ESize, JssStyle> = {
    [ESize.S]: {
        width: '32px',
        height: '32px',
        padding: '10px 8px !important',
        fontSize: '12px !important',
        lineHeight: '12px !important',
    },
    [ESize.M]: {
        width: '44px',
        height: '44px',
        padding: '12px 8px !important',
        fontSize: '16px !important',
        lineHeight: '18px !important',
    },
    [ESize.L]: {
        width: '52px',
        height: '52px',
        padding: '16px 12px !important',
        fontSize: '20px !important',
        lineHeight: '20px !important',
    },
};

export const paginationArrowBtnSizeStyles: Record<ESize, JssStyle> = {
    [ESize.S]: {
        width: '40px !important',
    },
    [ESize.M]: {
        width: '56px !important',
    },
    [ESize.L]: {
        width: '64px !important',
    },
};

export const sortingSizeStyles: Record<ESize, JssStyle> = {
    [ESize.S]: {
        maxWidth: '220px',
        minWidth: '100px',
        height: '35px',
        padding: '8px 10px',
        fontSize: '13px',
        lineHeight: '15px',
    },
    [ESize.M]: {
        maxWidth: '240px',
        minHWidth: '120px',
        height: '41px',
        padding: '10px 16px',
        fontSize: '15px',
        lineHeight: '17px',
    },
    [ESize.L]: {
        maxWidth: '260px',
        minHWidth: '140px',
        height: '57px',
        padding: '16px 22px',
        fontSize: '19px',
        lineHeight: '21px',
    },
};

export const pricingSizeStyles: Record<ESize, JssStyle> = {
    [ESize.S]: {
        height: '35px',
        padding: '8px 10px',
        fontSize: '13px',
        lineHeight: '15px',
    },
    [ESize.M]: {
        height: '41px',
        padding: '10px 16px',
        fontSize: '15px',
        lineHeight: '17px',
    },
    [ESize.L]: {
        height: '57px',
        padding: '16px 22px',
        fontSize: '19px',
        lineHeight: '21px',
    },
};

function getFontStyles(fontStyle: EFontStyle[]): JssStyle {
    return {
        fontWeight: fontStyle?.includes(EFontStyle.B) ? 'bold' : 'initial',
        fontStyle: fontStyle?.includes(EFontStyle.I) ? 'italic' : 'initial',
        textTransform: fontStyle?.includes(EFontStyle.AT) ? 'uppercase' : 'initial',
    };
}

function commonTypographySettings(data): JssStyle {
    const fontStyles: JssStyle = getFontStyles(data.fontStyle);

    return {
        ...fontStyles,
        color: themeColorCss(data.color as string),
        fontSize: `${data.fontSize}px`,
    };
}

function getFilterGeneralSettings(data: GeneralFilterSettings): JssStyle {
    const generalSettings: JssStyle = {
        backgroundColor: themeColorCss(data.backgroundColor as string),
        borderRadius: data.borderRadius,
        paddingTop: `${data.paddingTop}px`,
        paddingBottom: `${data.paddingBottom}px`,
        paddingLeft: `${data.paddingLeft}px`,
        paddingRight: `${data.paddingRight}px`,
    };

    if (data.isBorder) {
        generalSettings.borderWidth = `${data.borderWidth}px`;
        generalSettings.borderStyle = data.borderStyle;
        generalSettings.borderColor = themeColorCss(data.borderColor as string);
    }

    return generalSettings;
}

function getFilterPriceSettings(data: PriceFilterSettings): JssStyle {
    return {
        display: 'block',
        '& label': {
            ...commonTypographySettings(data.priceLabelFont),
            marginBottom: '10px',
            display: 'block',
        },

        '& .price-inputs-wrapper': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',

            '& span': {
                display: 'flex',
                color: themeColorCss(data.borderColor as string),
                padding: '0 8px',
            },

            '& input': {
                width: '100%',
                ...pricingSizeStyles[data.inputSize || ESize.M],
                '&:hover': {
                    borderColor: '#9a9a9a',
                },
                '&:focus': {
                    borderColor: '#82f6ff',
                    boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
                    outline: '0',
                },
                borderWidth: '1px',
                boxSizing: 'border-box',
                borderStyle: 'solid',
                borderTopWidth: `${data.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                borderRightWidth: `${data.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                borderLeftWidth: `${data.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                borderRadius: `${data.borderRadius}px`,
                background: themeColorCss(data.backgroundColor as string),
                borderColor: themeColorCss(data.borderColor as string),
                color: themeColorCss(data.textColor as string),
            },
        },

        '& .price-range': {
            display: 'block',
            position: 'relative',
            marginBottom: '12px',

            '& .range-double': {
                position: 'relative',
                height: '24px',
            },

            // Общий трек
            '& .range-track': {
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: '4px',
                transform: 'translateY(-50%)',
                borderRadius: '2px',
                backgroundColor: themeColorCss(data.colorLine),
            },

            // Заполненная часть между ползунками
            '& .range-progress': {
                position: 'absolute',
                top: 0,
                bottom: 0,
                borderRadius: '2px',
                backgroundColor: themeColorCss(data.colorFilledLine),
            },

            // Слои слайдеров
            '& input[type="range"]': {
                WebkitAppearance: 'none',
                appearance: 'none',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '25%',
                transform: 'translateY(-50%)',
                width: '100%',
                margin: 0,
                background: 'transparent',
                pointerEvents: 'auto',
                zIndex: 99,
                height: '4px',
                borderRadius: '2px',
                boxShadow: 'none',
                // скрываем дефолтный трек и рисуем своим контейнером
                '&::-webkit-slider-runnable-track': {
                    WebkitAppearance: 'none',
                    height: '4px',
                    background: 'transparent',
                },
                '&::-moz-range-track': {
                    height: '4px',
                    background: 'transparent',
                },

                // Ползунок (кнопка)
                '&::-webkit-slider-thumb': {
                    WebkitAppearance: 'none',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: themeColorCss(data.colorButtons),
                    border: `2px solid ${themeColorCss(data.colorButtons)}`,
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                },
                '&::-moz-range-thumb': {
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: themeColorCss(data.colorButtons),
                    border: `2px solid ${themeColorCss(data.colorButtons)}`,
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                },

                // Фокус/hover состояние ползунка
                '&:hover::-webkit-slider-thumb': {
                    filter: 'brightness(0.95)',
                },
                '&:hover::-moz-range-thumb': {
                    filter: 'brightness(0.95)',
                },
                '&:focus': {
                    outline: 'none',
                },
                '&:focus::-webkit-slider-thumb': {
                    boxShadow: '0 0 0 4px rgba(130,246,255,0.3)',
                },
                '&:focus::-moz-range-thumb': {
                    boxShadow: '0 0 0 4px rgba(130,246,255,0.3)',
                },
            },
        },
    };
}

function getCustomButtonStyle(data: ButtonSettings): JssStyle {
    return getBaseButtonData({ buttonBase: data.buttonBase, buttonHover: data.buttonHover });
}

function getCardBorder(settings: Settings): JssStyle {
    if (settings.card.isBorder) {
        return {
            borderWidth: `${settings.card.borderWidth}px`,
            borderStyle: settings.card.borderStyle,
            borderColor: themeColorCss(settings.card.borderColor as string),
        };
    }
}

export function getStyles(widget: WebLegacyWidgetEntity<CatalogProductsWidgetData>): JssStyle {
    const { pagination, basketButton, filter, sorting, settings, catalogView, itemsInRow } = widget.data;

    const catalogWrapper: JssStyle = {
        display: 'flex',
        flexDirection: catalogView === CatalogCategoriesView.top ? 'column' : 'row',
        width: '100%',
        '@media (max-width: 992px)': {
            flexDirection: 'column',
        },
        '&.mobileView': {
            flexDirection: 'column',
        },
        '& .filterWrapper': {
            width: catalogView === CatalogCategoriesView.top ? '100%' : '25%',
            minWidth: '220px',
            marginRight: catalogView === CatalogCategoriesView.top ? '0px' : '30px',
            marginBottom: catalogView === CatalogCategoriesView.top ? '10px' : '30px',
            display: 'flex',
            flexDirection: catalogView === CatalogCategoriesView.top ? 'row' : 'column',
            '& .categoriesWrapper': {
                marginBottom: '20px',
                marginRight: catalogView === CatalogCategoriesView.top ? '20px' : '0px',
                ...getFilterGeneralSettings(filter.general),
                '& label': {
                    ...commonTypographySettings(filter.category.categoryLabelFont),
                    marginBottom: '10px',
                    fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                    display: 'block',
                },
                '& ul': {
                    listStyle: 'none',
                    paddingLeft: '15px',
                    '&.categories-tree': {
                        paddingLeft: '0px',
                        '@media (max-width: 992px)': {
                            display: 'none',
                        },
                        '&.mobileView': {
                            display: 'none',
                        },
                    },
                    '& li': {
                        '& a': {
                            ...commonTypographySettings(filter.category.categoryFont),
                            fontWeight: 'normal',
                            textDecoration: 'none',
                            display: 'flex',
                            marginBottom: '10px',
                            fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8,
                            },
                            '&.active': {
                                ...commonTypographySettings(filter.category.selectedCategoryFont),
                                borderRight: `3px solid ${themeColorCss(filter.category.selectedCategoryFont.color as string)}`,
                            },
                            '&.non-selectable': {
                                cursor: 'default',
                                '&:hover': {
                                    opacity: 1,
                                },
                            },
                        },
                    },
                },
                '& .categories-dropdown-wrapper': {
                    display: catalogView === CatalogCategoriesView.top ? 'block' : 'none',
                    '& select': {
                        ...sortingSizeStyles[filter.category.selectFilterSettings.inputSize || ESize.M],
                        '&:hover': {
                            borderColor: '#9a9a9a',
                        },
                        '&:focus': {
                            borderColor: '#82f6ff',
                            boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
                            outline: '0',
                        },
                        borderWidth: '1px',
                        boxSizing: 'border-box',
                        borderStyle: 'solid',
                        whiteSpace: 'nowrap',
                        borderTopWidth: `${
                            filter.category.selectFilterSettings.borderStyle === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderRightWidth: `${
                            filter.category.selectFilterSettings.borderStyle === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderLeftWidth: `${
                            filter.category.selectFilterSettings.borderStyle === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderRadius: `${filter.category.selectFilterSettings.borderRadius}px`,
                        background: themeColorCss(filter.category.selectFilterSettings.backgroundColor as string),
                        borderColor: themeColorCss(filter.category.selectFilterSettings.borderColor as string),
                        color: themeColorCss(filter.category.selectFilterSettings.textColor as string),
                        '& option': {
                            background: themeColorCss(filter.category.selectFilterSettings.backgroundColor as string),
                            '&[disabled]': {
                                opacity: 0.5,
                                background: '#e7e7e7',
                            },
                        },
                    },
                    '@media (max-width: 992px)': {
                        display: 'block',
                    },
                    '&.mobileView': {
                        display: 'block',
                    },
                },
            },
            '& .priceFilter': {
                maxWidth: catalogView === CatalogCategoriesView.top ? '280px' : 'auto',
            },
        },
    };

    const cardsWrapper: JssStyle = {
        width: '100%',
        '& .cards-container': {
            display: 'flex',
            flexWrap: 'wrap',
            gap: `${settings.general.margin}px`,
        },
        '& .sorting-panel': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: `calc(${settings.general.margin}px - 10px)`,

            '& .total': {
                fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                fontSize: settings.typography.titleSettings.fontSize + 'px',
                color: themeColorCss(settings.typography.titleSettings.color as string),
            },
            '& .search input': {
                ...sortingSizeStyles[sorting.inputSize || ESize.M],
                '&:hover': {
                    borderColor: '#9a9a9a',
                },
                '&:focus': {
                    borderColor: '#82f6ff',
                    boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
                    outline: '0',
                },
                borderWidth: '1px',
                boxSizing: 'border-box',
                borderStyle: 'solid',
                whiteSpace: 'nowrap',
                borderTopWidth: `${sorting.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                borderRightWidth: `${sorting.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                borderLeftWidth: `${sorting.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                borderRadius: `${sorting.borderRadius}px`,
                background: themeColorCss(sorting.backgroundColor as string),
                borderColor: themeColorCss(sorting.borderColor as string),
                color: themeColorCss(sorting.textColor as string),
            },
            '& .sorting-wrapper': {
                '& select': {
                    ...sortingSizeStyles[sorting.inputSize || ESize.M],
                    '&:hover': {
                        borderColor: '#9a9a9a',
                    },
                    '&:focus': {
                        borderColor: '#82f6ff',
                        boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
                        outline: '0',
                    },
                    borderWidth: '1px',
                    boxSizing: 'border-box',
                    borderStyle: 'solid',
                    whiteSpace: 'nowrap',
                    borderTopWidth: `${sorting.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                    borderRightWidth: `${sorting.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                    borderLeftWidth: `${sorting.borderStyle === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
                    borderRadius: `${sorting.borderRadius}px`,
                    background: themeColorCss(sorting.backgroundColor as string),
                    borderColor: themeColorCss(sorting.borderColor as string),
                    color: themeColorCss(sorting.textColor as string),
                    '& option': {
                        background: themeColorCss(sorting.backgroundColor as string),
                        '&[disabled]': {
                            opacity: 0.5,
                            background: '#e7e7e7',
                        },
                    },
                },
            },
        },

        '@media (max-width: 480px)': {
            '& .sorting-panel': {
                flexWrap: 'wrap',
                '& .search': {
                    width: '100%',
                    marginBottom: '10px',

                    '& input': {
                        width: '100%',
                        maxWidth: '100% !important',
                    },
                },

                '& .sorting-wrapper': {
                    width: '100%',

                    '& select': {
                        maxWidth: '100% !important',
                        width: '100%',
                    },
                },
            },
        },
    };

    const cardWrapper: JssStyle = {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: themeColorCss(settings.card.backgroundColor as string),
        padding: `${settings.card.padding}px`,
        borderRadius: `${settings.card.borderRadius}px`,
        // width: `calc(100% / ${itemsInRow.desktop} - ${settings.general.margin}px)`,
        width: `calc((100% - (${settings.general.margin}px * (${itemsInRow.desktop} - 1))) / ${itemsInRow.desktop})`,
        ...getCardBorder(settings),
        '@media (max-width: 992px)': {
            width: `calc((100% - (${settings.general.margin}px * (${itemsInRow.mobile} - 1))) / ${itemsInRow.mobile})`,
        },
        '&.mobileView': {
            width: `calc((100% - (${settings.general.margin}px * (${itemsInRow.mobile} - 1))) / ${itemsInRow.mobile})`,
        },
        '& .image-wrapper.resizeble': {
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            paddingTop: '100%',
            marginBottom: `${settings.gallery.margin}px`,
            borderRadius: `${settings.gallery.borderRadius}px`,
            '& img': {
                width: '100%',
                height: '100%',
                position: 'absolute',
                inset: 0,
                objectFit: settings?.gallery?.attachment === Attachment.cover ? 'cover' : 'contain',
                margin: 'auto',
                userSelect: 'none',
            },
        },

        '& .image-wrapper': {
            marginBottom: `${settings.gallery.margin}px`,
            backgroundColor: themeColorCss(settings?.gallery?.backgroundColor as string),
            borderRadius: `${settings.gallery.borderRadius}px`,
            '& img': {
                width: '100%',
                height: 'auto',
            },
        },

        '& .blank-wrapper': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            marginBottom: `${settings.gallery.margin}px`,
            '& img': {
                borderRadius: `${settings.gallery.borderRadius}px`,
                width: '100%',
                height: 'auto',
            },
        },

        '& .card-title': {
            '& h2': {
                ...commonTypographySettings(settings.typography.titleSettings),
                marginBottom: '10px',
            },
            '& .card-counter': {
                ...commonTypographySettings(settings.typography.counterSettings),
                opacity: '0.7',
                fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
            },
        },

        '& .card-old-price': {
            ...commonTypographySettings(settings.typography.oldPriceSettings),
            textDecoration: 'line-through',
            fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
        },

        '& .card-price-block': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline',
            '& .card-new-price': {
                ...commonTypographySettings(settings.typography.priceSettings),
                marginRight: '10px',
                lineHeight: settings.typography.priceSettings.fontSize + 'px',
            },

            '& .card-old-price': {
                ...commonTypographySettings(settings.typography.oldPriceSettings),
                textDecoration: 'line-through',
                lineHeight: settings.typography.priceSettings.fontSize + 'px',
            },
        },
        '& .subscribe-block': {
            fontSize: settings.typography.priceSettings.fontSize * 0.8 + 'px',
        },
        '& .buttonWrapper': {
            display: 'flex',
            margin: '-10px',
            marginTop: `calc(${settings.gallery.margin}px - 15px)`,
        },
    };

    const basketButtonWrapper: JssStyle = {
        display: 'flex',
        alignItems: 'center',
    };

    const buttonStyles: JssStyle = {
        ...getCustomButtonStyle(basketButton),
    };

    const buttonIconStyles: JssStyle = {
        ...getBaseButtonIconStyles(settings.typography.titleSettings.fontSize),
        flexDirection: basketButton.buttonIcon?.position === EHorizontalPosition.L ? 'row' : 'row-reverse',
    };

    let nextButtonWidth: JssStyle = {};

    if (pagination?.nextPage?.widthType === EButtonWidthType.auto) {
        nextButtonWidth = {
            width: 'auto',
        };
    } else if (pagination?.nextPage?.widthType === EButtonWidthType.fixed) {
        nextButtonWidth = {
            width: '100%',
            maxWidth: pagination?.nextPage?.widthValue + 'px',
        };
    } else {
        nextButtonWidth = {
            width: '100%',
        };
    }

    const nextButtonSize: JssStyle = {
        padding: commonWebsitesFormButtonTextStyles[pagination?.nextPage?.size || ESize.M].padding,
        fontSize: commonWebsitesFormButtonTextStyles[pagination?.nextPage?.size || ESize.M].fontSize,
        lineHeight: commonWebsitesFormButtonTextStyles[pagination?.nextPage?.size || ESize.M].lineHeight,
    };

    let nextButtonStyles: JssStyle = {
        boxSizing: 'border-box',
        flexDirection: pagination?.nextPage?.buttonIcon?.position === EHorizontalPosition.L ? 'row' : 'row-reverse',
        ...nextButtonWidth,
        ...nextButtonSize,
    };

    if (pagination?.nextPage?.styleType === EButtonStyleType.custom) {
        nextButtonStyles = {
            ...nextButtonStyles,
            ...getBaseButtonData({
                buttonBase: pagination?.nextPage?.buttonBase,
                buttonHover: pagination?.nextPage?.buttonHover,
            }),
        };
    }

    const paginationStyles: JssStyle = {
        display: 'flex',
        alignItems: catalogView === CatalogCategoriesView.top ? 'center' : 'flex-start',
        flexDirection: 'column',
        marginTop: `${settings.general.margin}px`,

        '& ul': {
            listStyle: 'none',
            margin: 0,
            display: 'flex',
            gap: '10px',
            padding: 0,

            '& li': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '1px solid',
                backgroundColor: themeColorCss(pagination?.pagesButtons?.backgroundColor as string),
                borderColor: themeColorCss(pagination?.pagesButtons?.borderColor as string),
                borderRadius: `${pagination?.pagesButtons?.borderRadius}px`,
                ...paginationSizeStyles[pagination?.pagesButtons?.inputSize || ESize.M],
                '&.arrow-btn': {
                    ...paginationArrowBtnSizeStyles[pagination?.pagesButtons?.inputSize || ESize.M],
                },
                '& a': {
                    color: themeColorCss(pagination?.pagesButtons?.textColor as string),
                },
                '&.active': {
                    backgroundColor: themeColorCss(pagination?.currentPage?.backgroundColor as string),
                    borderColor: themeColorCss(pagination?.currentPage?.borderColor as string),
                    '& a': {
                        color: themeColorCss(pagination?.currentPage?.textColor as string),
                    },
                },
            },
        },

        '& .nextButtonWrapper': {
            display: 'flex',
            boxSizing: 'border-box',
            width: '300px',
            marginButton: `${pagination?.nextPage?.size === ESize.S ? '20px' : pagination?.nextPage?.size === ESize.M ? '24px' : '28px'}`,
        },
    };

    return {
        buttonStyles,
        cardWrapper,
        basketButtonWrapper,
        catalogWrapper,
        cardsWrapper,
        buttonIconStyles,
        nextButtonStyles,
        paginationStyles,
        priceFilter: getFilterPriceSettings(filter.price),
    };
}
